import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { correctionsApi } from '../../api/corrections'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatMoney } from '../../lib/format'
import type { PipelineTab } from '../../types/pipeline'
import { Correct } from '../../components/Correct'
import { STAGE_WORDS } from './stages'
import type { DealStage } from '../../types/deals'

// What Scout OFFERED rather than asserted. A counterparty who sells TO the firm — a banker, an auditor,
// an insurer, a recruiter, a software supplier — cannot hold a pipeline stage, but it is not hidden
// either: it sits here with the reason it was held back, one click from the pipeline and one click from
// gone. Recall lives in what is shown; precision lives in what is filed.

export function SuggestionsCard({ tab }: { tab: PipelineTab }) {
  const spoke = useSpoke()
  const q = useQuery({ queryKey: ['suggestions', spoke], queryFn: () => correctionsApi.suggestions(spoke), staleTime: 30_000 })
  const [all, setAll] = useState(false)
  const mine = (q.data?.suggestions ?? []).filter((s) => s.entityKind === (tab === 'leads' ? 'lead' : 'opportunity'))
  if (mine.length === 0) return null
  const rows = all ? mine : mine.slice(0, 8)

  return (
    <div className="card suggestions-card" id="suggestions">
      <div className="card-head">
        <div>
          <h2>Suggested, not filed <span className="count">{mine.length}</span></h2>
          <p className="muted">
            Threads that read like a pursuit but whose counterparty sells <strong>to</strong> the firm — a banker, an auditor, an insurer, a recruiter, a supplier.
            Scout will not put a guess on the board wearing the clothes of a fact, so these sit here with the reason instead.
            Put one back on the pipeline, or reject it and say why: the reason is what stops it coming back.
          </p>
        </div>
      </div>
      <table className="grid suggestions-grid">
        <thead>
          <tr><th>Company</th><th>What Scout would have said</th><th>Why it did not</th><th>Last activity</th><th>Your call</th></tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.entityId}>
              <td>
                <Link to={`/${tab}/${encodeURIComponent(s.entityId)}`}><strong>{s.companyName}</strong></Link>
                <span className="muted small">{s.subject}</span>
              </td>
              <td>
                <span className={`tag stage ${s.proposedStage}`}>{STAGE_WORDS[s.proposedStage as DealStage] ?? s.proposedStage}</span>
                {s.proposedAmount != null && <span className="muted small">{formatMoney(s.proposedAmount)} named, not counted</span>}
              </td>
              <td className="said">{s.why}</td>
              <td>{s.occurredOn ? formatDate(s.occurredOn) : '—'}</td>
              <td className="suggestion-call">
                <Correct kind={s.entityKind} entityId={s.entityId} entityLabel={`${s.companyName} · ${s.subject}`} currentValue="suggested" label="Judge it" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {mine.length > 8 && <button type="button" className="link-button small" onClick={() => setAll(!all)}>{all ? 'Show the first 8' : `Show all ${mine.length}`}</button>}
    </div>
  )
}
